// ---- Inventory (server-owned bag, client HUD mirror — bottom-right 10 slots) ----
// Renders what the player is carrying: a fixed array of 10 slots, each either null
// or a { type, count } STACK. Most items are unstackable (stack:1 → one per slot);
// ammo/consumables stack (arrows: stack:99), so a slot shows an "×N" count badge.
// The panel toggles from a backpack button (and the I key, wired in input.ts).
//
// As of Step 2 the SERVER owns the bag (index.ts + src/server/bag.ts). This module
// is a render MIRROR: `slots` is replaced wholesale by the server's `inventory`
// message (setInventory) / the join `spawn` (hydrateInventory), and the pickup /
// drop / equip / fire buttons are INTENT senders — the server validates each,
// mutates the authoritative bag, and echoes the new state back. hasFreeSlot/countOf
// remain client-side UX hints (the server re-validates); equipping is optimistic for
// snappy feel and the bag echo drops the item from the grid. Held items (weapon /
// offhand / tool) and worn gear live OUTSIDE the bag — they free a bag slot while
// equipped and only return on unequip (same rule as wear).

import { $ } from "./dom.ts";
import { game } from "./state.ts";
import { itemSpec } from "./items/index.ts";
import { isWeapon, isWearable, isShield, isFishing, isChopping, isFood, isCookable, LOG_ITEM_TYPES, stackSize, type WeaponSpec, type FishingSpec } from "./items/define.ts";
import { hasBoxUnlocked } from "./skill.ts";
import { frontOf, type ItemType } from "./shared.ts";

const SLOTS = 10;
type Slot = { type: ItemType; count: number } | null;
/** One occupied slot — the wire shape the server sends in `inventory` / `spawn`. */
export type BagEntry = { slot: number; type: ItemType; count: number };
const slots: Slot[] = Array(SLOTS).fill(null);
let open = false;
let selected: number | null = null;
// Held channels — type mirrors of NetPlayer.weapon/offhand/tool. NOT bag indices:
// equip moves the item bag → hand server-side (sendInventory drops it from slots).
let weapon: ItemType | null = null;
// The offhand-shield mirror (plan 14).
let offhand: ItemType | null = null;
// The held-tool mirror (plan 10 — fishing rods / hatchets). Main-hand exclusive with weapon.
let tool: ItemType | null = null;

let panel: HTMLElement;
let toggleBtn: HTMLElement;
let slotsWrap: HTMLElement;
let actionsEl: HTMLElement;
let dropBtn: HTMLButtonElement;
let equipBtn: HTMLButtonElement;
let wearBtn: HTMLButtonElement;
let eatBtn: HTMLButtonElement;
let cookInvBtn: HTMLButtonElement;
let fireStartBtn: HTMLButtonElement;
let toastEl: HTMLElement;
// Drop INTENT: asks the server to drop one unit at (x,y,z). Equip INTENT: asks the
// server to equip/unequip + attaches the weapon to the local rig for feel. Wear
// INTENT: asks the server to move a wearable bag → body slot (gear.ts mirrors the echo).
// Eat/Cook INTENTS (plan 18): consume a food dish / start cooking a raw ingredient
// (cooking.ts owns the loop; injected to avoid an inventory↔cooking import cycle).
let onDrop: (type: ItemType, x: number, y: number, z: number) => void = () => {};
let onEquip: (type: ItemType | null) => void = () => {};
let onWear: (type: ItemType) => void = () => {};
let onEquipOff: (type: ItemType | null) => void = () => {};
let onEquipTool: (type: ItemType | null) => void = () => {};
let onEat: (type: ItemType) => void = () => {};
let onCook: (type: ItemType) => void = () => {};
let onFireStart: () => void = () => {};
// Tutorial coach: item type whose bag slot(s) should pulse gold (src/coach.ts).
let coachItem: ItemType | null = null;
// Paperdoll (gear.ts) re-paints wear + equip cells whenever the bag equip state
// changes — installed by initGear so inventory stays free of a hard cycle.
let paperdollHook: (() => void) | null = null;
export function setPaperdollHook(fn: () => void): void {
  paperdollHook = fn;
}
function notifyPaperdoll(): void {
  paperdollHook?.();
}

export function initInventory(
  dropHandler: (type: ItemType, x: number, y: number, z: number) => void,
  equipHandler: (type: ItemType | null) => void = () => {},
  wearHandler: (type: ItemType) => void = () => {},
  equipOffHandler: (type: ItemType | null) => void = () => {},
  equipToolHandler: (type: ItemType | null) => void = () => {},
  eatHandler: (type: ItemType) => void = () => {},
  cookHandler: (type: ItemType) => void = () => {},
  fireStartHandler: () => void = () => {},
): void {
  onDrop = dropHandler;
  onEquip = equipHandler;
  onWear = wearHandler;
  onEquipOff = equipOffHandler;
  onEquipTool = equipToolHandler;
  onEat = eatHandler;
  onCook = cookHandler;
  onFireStart = fireStartHandler;
  panel = $<HTMLElement>("#inventory");
  toggleBtn = $<HTMLElement>("#invToggle");
  slotsWrap = $<HTMLElement>("#invslots");
  actionsEl = $<HTMLElement>("#invActions");
  dropBtn = $<HTMLButtonElement>("#dropBtn");
  equipBtn = $<HTMLButtonElement>("#equipBtn");
  wearBtn = $<HTMLButtonElement>("#wearBtn");
  eatBtn = $<HTMLButtonElement>("#eatBtn");
  cookInvBtn = $<HTMLButtonElement>("#cookInvBtn");
  fireStartBtn = $<HTMLButtonElement>("#fireStartBtn");
  toastEl = $<HTMLElement>("#toast");

  // Build the 10 slot cells once; render() fills their contents.
  for (let i = 0; i < SLOTS; i++) {
    const cell = document.createElement("button");
    cell.className = "invslot";
    cell.type = "button";
    cell.dataset.i = String(i);
    // detail>1 = second half of a double-click — skip so we don't toggle the
    // selection off before dblclick runs the primary action.
    cell.addEventListener("click", (e) => { if (e.detail <= 1) selectSlot(i); });
    cell.addEventListener("dblclick", (e) => {
      e.preventDefault();
      primaryActionOnSlot(i);
    });
    slotsWrap.appendChild(cell);
  }

  toggleBtn.addEventListener("click", toggleInventory);
  dropBtn.addEventListener("click", dropSelected);
  equipBtn.addEventListener("click", toggleEquipSelected);
  fireStartBtn.addEventListener("click", () => onFireStart());
  wearBtn.addEventListener("click", wearSelected);
  eatBtn.addEventListener("click", eatSelected);
  cookInvBtn.addEventListener("click", cookSelected);
  render();
}

/** The equipped weapon's type, or null. */
export function equippedType(): ItemType | null {
  return weapon;
}

/** The equipped weapon's combat data, or null (used by combat.ts). */
export function equippedWeapon(): WeaponSpec | null {
  const t = equippedType();
  return t ? itemSpec(t).weapon ?? null : null;
}

/** The equipped offhand shield's type, or null (plan 14 — used by combat.ts's
 *  block input; the server owns the truth). */
export function equippedShieldType(): ItemType | null {
  return offhand;
}

/** The held tool's type (rod or hatchet), or null — the held-tool channel. */
export function equippedTool(): ItemType | null {
  return tool;
}

/** The held fishing rod's type only — null if empty-handed or a non-rod tool
 *  (e.g. hatchet) is equipped. Fishing UI/cast gates MUST use this, not
 *  equippedTool(), so a hatchet never enables casting. */
export function equippedRodType(): ItemType | null {
  if (!tool) return null;
  return isFishing(itemSpec(tool)) ? tool : null;
}

/** The held rod's fishing data, or null (used by fishing.ts). */
export function equippedRod(): FishingSpec | null {
  const t = equippedRodType();
  return t ? itemSpec(t).fishing ?? null : null;
}

/** Fill the 10 slots from a bag snapshot (shared by hydrate + mirror update). */
function layout(bag: BagEntry[]): void {
  for (let i = 0; i < SLOTS; i++) slots[i] = null;
  for (const b of bag) {
    if (b.slot >= 0 && b.slot < SLOTS) slots[b.slot] = { type: b.type, count: b.count };
  }
}

/** Initial load on join (the `spawn` message): bag cargo + held channels (already
 *  removed from the bag server-side). Silent — the caller seats meshes on the rig. */
export function hydrateInventory(
  bag: BagEntry[],
  held: { weapon: ItemType | null; offhand?: ItemType | null; tool?: ItemType | null },
): void {
  layout(bag);
  selected = null;
  weapon = held.weapon;
  offhand = held.offhand ?? null;
  tool = held.tool ?? null;
  render();
}

/** Mirror an authoritative bag snapshot from the server (`inventory` message).
 *  Held items live outside the bag — a missing weapon type is normal while
 *  equipped; do NOT clear hands from bag absence. Preserves selection. */
export function setInventory(bag: BagEntry[]): void {
  const prevSel = selected;
  layout(bag);
  selected = prevSel !== null && slots[prevSel] ? prevSel : null;
  render();
}

export function toggleInventory(): void {
  open = !open;
  panel.style.display = open ? "block" : "none";
  toggleBtn.classList.toggle("active", open);
  if (!open) selectSlot(null);
  // Coach phases bag-button ↔ slot glow on open/close (lazy to avoid a cycle).
  void import("./coach.ts").then((m) => m.refreshCoach());
}

/** Programmatic open/close (cooking.ts runs the bag alongside the pan, plan 25). */
export function openInventory(): void {
  if (!open) toggleInventory();
}
export function closeInventory(): void {
  if (open) toggleInventory();
}

/** Whether the bag panel is open (coach + cooking gate off this). */
export function inventoryOpen(): boolean {
  return open;
}

/** Currently selected bag item type, or null (coach `select` phase). */
export function selectedType(): ItemType | null {
  if (selected === null) return null;
  return slots[selected]?.type ?? null;
}

/** Item type the quest coach wants highlighted in the bag, or null. */
export function coachGlowItem(): ItemType | null {
  return coachItem;
}

/** Set (or clear) the bag-slot coach glow. Inventory re-renders so slots update. */
export function setCoachGlowItem(type: ItemType | null): void {
  if (coachItem === type) return;
  coachItem = type;
  if (slotsWrap) render();
}

/** Is there room to pick something up? Checked before we ask the server. A
 *  stackable type with an existing, non-full stack always has room. */
export function hasFreeSlot(type?: ItemType): boolean {
  if (slots.includes(null)) return true;
  if (!type) return false;
  const cap = stackSize(itemSpec(type));
  return slots.some((s) => s !== null && s.type === type && s.count < cap);
}

/** How many of an item type we're carrying (summed across stacks). Used to gate
 *  firing on ammo + show the arrow count in the action bar. */
export function countOf(type: ItemType): number {
  let n = 0;
  for (const s of slots) if (s && s.type === type) n += s.count;
  return n;
}

/** Snapshot of the 10 bag cells for secondary UIs (gift modal bag mirror, …).
 *  Read-only copy — never mutate the returned entries. */
export function bagSlotSnapshot(): ReadonlyArray<{ type: ItemType; count: number } | null> {
  return slots.map((s) => (s ? { type: s.type, count: s.count } : null));
}

// Pocketing (pickup) + consuming (ammo per shot) now happen SERVER-side; the mirror
// updates from the `inventory` message. There is no local addItem/consumeItem.

function selectSlot(i: number | null): void {
  // Clicking an empty slot, or the selected one again, clears the selection.
  if (i !== null && (slots[i] === null || i === selected)) i = null;
  selected = i;
  render();
  // Coach advances bag-glow → world arrow when the tutorial item is selected.
  void import("./coach.ts").then((m) => m.refreshCoach());
}

/** Double-click a bag slot → same primary as the dock's first button (equip /
 *  wear / eat / cook / start fire). Drop stays a deliberate single-click action. */
function primaryActionOnSlot(i: number): void {
  const s = slots[i];
  if (!s) return;
  selected = i;
  const spec = itemSpec(s.type);
  if (isWeapon(spec) || isShield(spec) || isFishing(spec) || isChopping(spec)) {
    toggleEquipSelected(); // renders itself
  } else {
    if (isWearable(spec)) wearSelected();
    else if (isFood(spec)) eatSelected();
    else if (isCookable(spec)) cookSelected();
    else if (s.type === "kindling" && LOG_ITEM_TYPES.some((t) => countOf(t as ItemType) > 0)) {
      onFireStart();
    }
    // No primary (e.g. raw materials) still selects so Drop is ready.
    render();
  }
  void import("./coach.ts").then((m) => m.refreshCoach());
}

function dropSelected(): void {
  if (selected === null) return;
  const s = slots[selected];
  if (!s) return;
  // Intent only: the server peels one unit off the authoritative bag, spawns the
  // world item, and echoes the new bag. Held/worn items are not bag cargo —
  // unequip first to drop them.
  const p = game.player.position;
  const { x, z } = frontOf(p.x, p.z, game.player.rotation.y);
  onDrop(s.type, x, game.feetY, z);
}

/** Human label for a skill-box gate toast (kebab → Title Case). */
function boxLabel(boxId: string): string {
  return boxId.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/** Gate equip intents that the server will reject for a missing skill box —
 *  client is optimistic, so without this we show a bow in-hand while the
 *  server still has a sword (fire silently no-ops; refresh restores the sword). */
function equipBoxGate(boxId: string | undefined): boolean {
  if (!boxId || hasBoxUnlocked(boxId)) return true;
  flashToast(`Unlock ${boxLabel(boxId)} first (Skills book · J)`);
  return false;
}

/** Equip the selected bag item onto a held channel. The item leaves the bag on
 *  the server echo (sendInventory). Unequip is paperdoll-only (item is no longer
 *  a bag row). Shields (plan 14) → offhand; rods/hatchets → tool; weapons → main. */
function toggleEquipSelected(): void {
  if (selected === null) return;
  const s = slots[selected];
  if (!s) return;
  const spec = itemSpec(s.type);
  if (isShield(spec)) {
    if (equippedWeapon()?.grip === "two-hand") {
      flashToast("Two-handed weapons need both hands");
      return;
    }
    if (!equipBoxGate(spec.shield.requiresBox)) return;
    // Already holding this type (e.g. a second shield in bag) — still a re-equip
    // intent; server no-ops if identical. Swap path when a different shield is on.
    setOffhand(s.type);
    render();
    return;
  }
  // Held tools (rod / hatchet): clear the weapon FIRST so setWeapon(null)/equipRig
  // cannot wipe the hatchet's synthetic weaponSpec after equipTool seats it.
  if (isFishing(spec) || isChopping(spec)) {
    const need = isFishing(spec) ? spec.fishing.requiresBox
      : isChopping(spec) ? spec.chopping.requiresBox : undefined;
    if (!equipBoxGate(need)) return;
    if (weapon) setWeaponType(null);
    setToolType(s.type);
    render();
    return;
  }
  if (!isWeapon(spec)) return;
  if (!equipBoxGate(spec.weapon.requiresBox)) return;
  if (!equipBoxGate(spec.hunting?.requiresBox)) return;
  if (tool) setToolType(null); // main-hand exclusive
  // Two-hander clears the offhand (server returns the shield to the bag when the
  // equip is accepted — free-slot math lives there; optimistic mirror here).
  if (spec.weapon.grip === "two-hand" && offhand) setOffhand(null);
  setWeaponType(s.type);
  render();
}

/** Set (or clear) the offhand shield mirror and notify the equip-off handler. */
function setOffhand(type: ItemType | null): void {
  if (offhand === type) return;
  offhand = type;
  onEquipOff(type);
}

/** Set (or clear) the held-tool mirror and notify the equip-tool handler. */
function setToolType(type: ItemType | null): void {
  if (tool === type) return;
  tool = type;
  onEquipTool(type);
  // Coach advances equip → world-arrow the moment the rod is in hand.
  // Lazy import avoids a static inventory↔coach cycle.
  void import("./coach.ts").then((m) => m.refreshCoach());
}

/** Set (or clear) the main-hand weapon mirror and notify the equip handler. */
function setWeaponType(type: ItemType | null): void {
  if (weapon === type) return;
  weapon = type;
  onEquip(type);
}

/** Wear the selected wearable (intent — the server moves it bag → body slot,
 *  swapping back anything already worn there, and echoes bag + gear). */
function wearSelected(): void {
  if (selected === null) return;
  const s = slots[selected];
  if (!s || !isWearable(itemSpec(s.type))) return;
  onWear(s.type);
}

/** Eat the selected food (plan 18) — heal/stamina via the consume intent. Kept
 *  open so several can be eaten in a row (the server echo re-renders the count). */
function eatSelected(): void {
  if (selected === null) return;
  const s = slots[selected];
  if (!s || !isFood(itemSpec(s.type))) return;
  onEat(s.type);
}

/** Hearth gate for the Cook button (plan 25) — cooking.ts injects the real
 *  check (near a range/campfire, or already in cook mode) via setCookGate so
 *  hearth logic keeps one home; defaults open until wired. */
let cookGate: () => boolean = () => true;
export function setCookGate(fn: () => boolean): void {
  cookGate = fn;
}

/** Cook the selected raw ingredient (plan 25). The bag STAYS open — cook mode
 *  runs the pan alongside it, and the post-result loop picks the next dish here. */
function cookSelected(): void {
  if (selected === null) return;
  const s = slots[selected];
  if (!s || !isCookable(itemSpec(s.type))) return;
  // Re-check the hearth gate at CLICK time, not just at render: the button stays
  // clickable when out of reach (a native-disabled button can't re-validate after
  // the player walks up to a range) — so a stale "needs a fire" label still cooks
  // once they're actually in range.
  if (!cookGate()) { flashToast("You need a fire — find a kitchen range or start a campfire"); return; }
  onCook(s.type);
}

/** Clear the main-hand weapon (paperdoll click). Needs a free bag slot to return it. */
export function unequipMain(): void {
  if (!weapon) return;
  if (!hasFreeSlot()) { flashToast("Inventory full"); return; }
  setWeaponType(null);
  render();
}

/** Clear the offhand shield (paperdoll click). Needs a free bag slot to return it. */
export function unequipOffhand(): void {
  if (!offhand) return;
  if (!hasFreeSlot()) { flashToast("Inventory full"); return; }
  setOffhand(null);
  render();
}

/** Clear the held tool (paperdoll click). Needs a free bag slot to return it. */
export function unequipTool(): void {
  if (!tool) return;
  if (!hasFreeSlot()) { flashToast("Inventory full"); return; }
  setToolType(null);
  render();
}

/** Flash a brief message bottom-center (e.g. "Inventory full"). */
let toastTimer: ReturnType<typeof setTimeout> | undefined;
export function flashToast(msg: string): void {
  toastEl.textContent = msg;
  toastEl.classList.add("show");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toastEl.classList.remove("show"), 1400);
}

function render(): void {
  const cells = slotsWrap.children;
  for (let i = 0; i < SLOTS; i++) {
    const cell = cells[i] as HTMLElement;
    const s = slots[i];
    // Icon glyph + an "×N" count badge for stacks > 1.
    cell.textContent = s ? itemSpec(s.type).icon : "";
    if (s && s.count > 1) {
      const badge = document.createElement("span");
      badge.className = "invcount";
      badge.textContent = `×${s.count}`;
      cell.appendChild(badge);
    }
    cell.classList.toggle("filled", s !== null);
    cell.classList.toggle("sel", i === selected);
    // Held items leave the bag — no "equipped" glow on cargo slots.
    cell.classList.toggle("equipped", false);
    cell.classList.toggle("coach-glow", !!(coachItem && s && s.type === coachItem));
  }
  // Keep the paperdoll equip cells in lockstep with bag equip state.
  notifyPaperdoll();
  const selSlot = selected !== null ? slots[selected] : null;
  const selType = selSlot?.type ?? null;
  // Action dock is fixed-height (CSS). Toggle `.show` / `.has-sel` only — never
  // display:none, or the panel reflows when Equip/Drop appear.
  actionsEl.classList.toggle("has-sel", !!selType);
  const show = (btn: HTMLElement, on: boolean) => btn.classList.toggle("show", on);

  // Drop: always available for a filled selection (second dock row).
  if (selType) {
    dropBtn.textContent = `Drop ${itemSpec(selType).name}`;
    show(dropBtn, true);
  } else {
    dropBtn.textContent = "";
    show(dropBtn, false);
  }
  // Primary row: at most one of Equip / Wear / Eat / Cook / Start fire.
  // Held items leave the bag, so bag actions are always Equip (unequip = paperdoll).
  let equipOn = false;
  if (selType && isWeapon(itemSpec(selType))) {
    equipBtn.textContent = `Equip ${itemSpec(selType).name}`;
    equipOn = true;
  } else if (selType && isShield(itemSpec(selType))) {
    equipBtn.textContent = `Equip ${itemSpec(selType).name}`;
    equipOn = true;
  } else if (selType && (isFishing(itemSpec(selType)) || isChopping(itemSpec(selType)))) {
    equipBtn.textContent = `Equip ${itemSpec(selType).name}`;
    equipOn = true;
  } else {
    equipBtn.textContent = "";
  }
  show(equipBtn, equipOn);
  // Coach: soft border glow on Equip when the tutorial item is selected and not yet on.
  {
    const wantEquip = !!coachItem && !!selType && equipOn && (
      ((isFishing(itemSpec(selType)) || isChopping(itemSpec(selType))) && tool !== selType)
      || (isWeapon(itemSpec(selType)) && weapon !== selType)
    );
    equipBtn.classList.toggle("coach-glow", wantEquip);
  }
  if (selType && isWearable(itemSpec(selType))) {
    wearBtn.textContent = `Wear ${itemSpec(selType).name}`;
    show(wearBtn, true);
  } else {
    wearBtn.textContent = "";
    show(wearBtn, false);
  }
  if (selType && isFood(itemSpec(selType))) {
    eatBtn.textContent = `Eat ${itemSpec(selType).name}`;
    show(eatBtn, true);
  } else {
    eatBtn.textContent = "";
    show(eatBtn, false);
  }
  if (selType && isCookable(itemSpec(selType))) {
    // Stay CLICKABLE even out of hearth reach — cookSelected re-checks the gate
    // live (walking to a range doesn't re-render the bag), just dim the label as
    // a hint. A native `disabled` here strands the button until a reselect.
    const atFire = cookGate();
    cookInvBtn.textContent = atFire ? `Cook ${itemSpec(selType).name}` : "Cook (needs a fire)";
    cookInvBtn.style.opacity = atFire ? "1" : "0.55";
    show(cookInvBtn, true);
  } else {
    cookInvBtn.textContent = "";
    cookInvBtn.style.opacity = "1";
    show(cookInvBtn, false);
  }
  // Start fire: kindling selected + any log in the bag (firestarting activity).
  if (selType === "kindling" && LOG_ITEM_TYPES.some((t) => countOf(t as ItemType) > 0)) {
    fireStartBtn.textContent = "Start fire";
    show(fireStartBtn, true);
  } else {
    fireStartBtn.textContent = "";
    show(fireStartBtn, false);
  }
}
